// Companion/blocks/bodies/QuestionBlockBody/LinkedFlashcardsModal.tsx
//
// The linked flashcards of a missed question, previewed and curated before the
// student studies them (ORA-832).
//
// QBank's review screen has this as a tab (`Study/Exam/components/
// LinkedFlashcards`): the cards the miss assigned, each with a switch, plus a
// master switch. The chat needs the same thing in front of its badge, so the
// copy and the toggle semantics are QBank's — the markup is not. That tab is
// legacy Sass built to fill a panel (`height: 100%`, an absolutely positioned
// pill, `.in-exam.dark` scoping), none of which survives being dropped into a
// modal over the thread.
//
// The switches write through the same `POST /api/v5/flashcards/priority` the
// tab uses, immediately, so the curation is the student's scheduling and not a
// draft: "Study Now" then asks for the cards that are left on.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Clock } from '@phosphor-icons/react';
import { SuspendedCards as SuspendedCardsIcon } from '@/assets/svgs/SuspendedCards';
import { Button } from '@/components/ui/button';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { updateLinkedFlashcardAssignments } from '@/services/flashcards';
import type { Flashcard } from '@/types/Flashcard';
import { getFlashcardVariants } from '@/utils/flashcardVariants';
import { parseHtmlWithMathLatex } from '@/utils/htmlParser';

/** QBank's own words for what this list is, verbatim. */
export const LINKED_FLASHCARDS_DESCRIPTION =
  'Linked flashcards of incorrectly answered vignettes are automatically assigned for review. You can make changes here.';

const TOGGLE_FAILED = 'That change could not be saved. Try again.';

/** QBank's words for a card this miss put into the student's rotation. */
const ALREADY_ASSIGNED_TITLE =
  'This flashcard is already assigned. You will see it in the future.';

/**
 * A card the student learned somewhere else: the read behind the badge returns
 * every card linked to the question, and only the ones the miss assigned are
 * part of this review.
 */
const ALREADY_LEARNED_TITLE =
  'You have already learned this flashcard. It is not part of this review.';

const NOTHING_ENABLED = 'Enable at least one card to study.';
const NO_CARDS = 'No linked flashcards for this question.';

/** What one card of the list draws, once the unrenderable ones are dropped. */
interface LinkedCardRow {
  id: string;
  /** The card's front, as authored: HTML with clozes, MathML and images. */
  front: string;
  /**
   * Whether the student may turn this card off. QBank offers the switch on a
   * card this miss newly assigned; a card that was already in the rotation is
   * not this question's to unassign, and a suspended one is governed by the
   * suspension instead.
   */
  isToggleable: boolean;
  isSuspended: boolean;
}

export interface LinkedFlashcardsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The missed question the cards are linked to; every write names it. */
  vignetteId: string;
  /** Every card the read returned — assigned or not, so both can be toggled. */
  cards: Flashcard[];
  /** Study the cards left enabled, here in the thread. */
  onStudyNow: (flashcardIds: string[]) => void;
  /**
   * The student moved a switch. The caller uses it to drop what it was told
   * about the selection before this one — a refusal of it, or the deck a
   * previous "Study Now" went back to.
   */
  onCurationChanged?: () => void;
  /**
   * Whether a priority write is in flight. Nothing cancels one, so a caller that
   * re-reads the assignments has to wait for it rather than race it.
   */
  onWriteIsPendingChange?: (isPending: boolean) => void;
  /** The study request is in flight; the button says so and refuses a second. */
  isStudyNowPending?: boolean;
  /** Why the study request was refused, when the caller knows. */
  studyNowError?: string | null;
  /**
   * Something the caller wants said about the last "Study Now" that is not a
   * refusal — it went back to content the thread already held.
   */
  studyNowNotice?: string | null;
  /**
   * What studying them will produce, when it is not the rateable deck the copy
   * would otherwise imply — a single served question has no set to mint a
   * review set from, so its cards arrive as a flip-through block instead.
   */
  studyNowNote?: string;
  /**
   * The cards behind the button are gone for good. The list stays — it is what
   * the student is reading — but it stops offering to study them.
   */
  studyNowUnavailable?: boolean;
}

const toRow = (card: Flashcard): LinkedCardRow[] => {
  const front = getFlashcardVariants(card)[0]?.front;
  // A card with no front cannot be previewed or studied; QBank's own list
  // drops it the same way rather than drawing an empty switch.
  if (!front) return [];

  const isSuspended = Boolean(card.scheduling?.isSuspended ?? card.isSuspended);

  return [
    {
      id: card.id,
      front,
      isToggleable: card.isNew === true && !isSuspended,
      isSuspended,
    },
  ];
};

const LinkedFlashcardsModal = ({
  isOpen,
  onClose,
  vignetteId,
  cards,
  onStudyNow,
  onCurationChanged,
  onWriteIsPendingChange,
  isStudyNowPending = false,
  studyNowError,
  studyNowNote,
  studyNowNotice,
  studyNowUnavailable = false,
}: LinkedFlashcardsModalProps) => {
  /** Which cards are assigned, as the switches show it. */
  const [assignments, setAssignments] = useState<Record<string, boolean>>({});
  const [savingIds, setSavingIds] = useState<Record<string, true>>({});
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const rows = useMemo(() => cards.flatMap(toRow), [cards]);

  // What the server says the assignments are, as a value that changes only when
  // they do — so opening the modal seeds from live scheduling without a fresh
  // array identity from the parent resetting a switch the student just flipped.
  const seedKey = cards
    .map((card) => `${card.id}:${card.isLinked === true}`)
    .join('|');

  // Read through a ref so a write settling does not itself re-seed: the
  // deferred re-read changes `seedKey` when it lands, and that is the moment to
  // seed. Seeding while a write is in flight would show the pre-curation read.
  const writeIsPendingRef = useRef(false);

  useEffect(() => {
    if (!isOpen || writeIsPendingRef.current) return;

    setAssignments(
      Object.fromEntries(cards.map((card) => [card.id, card.isLinked === true]))
    );

    setToggleError(null);
    // `cards` is read through `seedKey`, which is what actually changes; the
    // array itself is a fresh identity on every parent render.
  }, [isOpen, seedKey]);

  const toggleableRows = rows.filter((row) => row.isToggleable);
  const isSavingOne = Object.keys(savingIds).length > 0;
  const writeIsPending = isSavingAll || isSavingOne;
  writeIsPendingRef.current = writeIsPending;

  // Told rather than inferred: the caller re-reads the assignments when this
  // closes, and that read has to follow the last write.
  useEffect(() => {
    onWriteIsPendingChange?.(writeIsPending);
  }, [onWriteIsPendingChange, writeIsPending]);

  /**
   * Whether the master switch reads as on. QBank's rule: it speaks for the
   * cards it can actually flip, so a list of already-assigned cards leaves it
   * out of the header rather than showing a switch that does nothing.
   */
  const allAssigned =
    toggleableRows.length > 0 &&
    toggleableRows.every((row) => assignments[row.id] === true);

  const enabledIds = rows
    .filter((row) => assignments[row.id] === true)
    .map((row) => row.id);

  const write = async (
    changes: Array<{ flashcardId: string; isLinked: boolean }>
  ): Promise<boolean> => {
    try {
      const response = await updateLinkedFlashcardAssignments(
        changes.map((change) => ({ ...change, vignetteId }))
      );

      return response?.success === true;
    } catch {
      return false;
    }
  };

  /**
   * Flip one card, immediately. The switch moves first — a write that takes a
   * moment must not make the control feel broken — and moves back if the write
   * does not land, because the switch is a claim about the student's schedule.
   */
  const handleToggle = async (flashcardId: string) => {
    const next = assignments[flashcardId] !== true;

    setToggleError(null);
    onCurationChanged?.();
    setAssignments((previous) => ({ ...previous, [flashcardId]: next }));
    setSavingIds((previous) => ({ ...previous, [flashcardId]: true }));

    const saved = await write([{ flashcardId, isLinked: next }]);

    setSavingIds((previous) => {
      const remaining = { ...previous };
      delete remaining[flashcardId];
      return remaining;
    });

    if (saved) return;

    setAssignments((previous) => ({ ...previous, [flashcardId]: !next }));
    setToggleError(TOGGLE_FAILED);
  };

  /** The same, for every card the master switch speaks for. */
  const handleToggleAll = async () => {
    if (!toggleableRows.length) return;

    const next = !allAssigned;
    const previous = assignments;

    setToggleError(null);
    onCurationChanged?.();

    setAssignments((current) => ({
      ...current,
      ...Object.fromEntries(toggleableRows.map((row) => [row.id, next])),
    }));

    setIsSavingAll(true);

    const saved = await write(
      toggleableRows.map((row) => ({ flashcardId: row.id, isLinked: next }))
    );

    setIsSavingAll(false);

    if (saved) return;

    setAssignments(previous);
    setToggleError(TOGGLE_FAILED);
  };

  const canStudy =
    enabledIds.length > 0 && !isStudyNowPending && !studyNowUnavailable;

  // One line under the list, and a refusal outranks everything else there.
  const notice = studyNowError ?? toggleError ?? null;
  const info = notice ? null : (studyNowNotice ?? null);

  const hint =
    notice || info
      ? null
      : (studyNowNote ?? (enabledIds.length ? null : NOTHING_ENABLED));

  return (
    <Modal
      className="modal--glass"
      name="linked-flashcards"
      onClose={onClose}
      open={isOpen}
    >
      <ModalHeader className="modal_header">
        <div className="flex min-w-0 flex-col gap-1">
          <h2>Linked Flashcards</h2>
          <p className="!m-0 text-xs text-fg-secondary">
            {LINKED_FLASHCARDS_DESCRIPTION}
          </p>
        </div>
        {toggleableRows.length ? (
          <Switch
            aria-label={
              allAssigned
                ? 'Unassign every linked flashcard'
                : 'Assign every linked flashcard'
            }
            checked={allAssigned}
            context="modal"
            // A row write in flight would be rolled back by this one's own
            // rollback, so the two never overlap.
            disabled={isSavingAll || isSavingOne}
            onChange={() => void handleToggleAll()}
          />
        ) : null}
      </ModalHeader>
      <ModalBody className="modal_content">
        <div className="flex flex-col gap-2">
          {rows.length ? (
            rows.map((row, index) => {
              const isAssigned = assignments[row.id] === true;

              return (
                <div
                  className={cn(
                    'flex items-start justify-between gap-3 rounded-xl border border-modal-glass-border bg-modal-field px-3 py-3 text-modal-fg transition-opacity',
                    // Dimmed means "you turned this one off". A row the student
                    // cannot flip is not off, so it reads at full strength.
                    row.isToggleable && !isAssigned && 'opacity-60'
                  )}
                  key={row.id}
                >
                  {/* Card copy is authored HTML and can run long, so each row
                    scrolls inside its own bound rather than pushing the list. */}
                  <div className="max-h-32 min-w-0 flex-1 overflow-y-auto text-sm leading-normal">
                    {parseHtmlWithMathLatex(row.front)}
                  </div>
                  <div className="flex min-h-11 w-11 flex-none items-center justify-center">
                    {row.isToggleable ? (
                      <Switch
                        aria-label={`${
                          isAssigned ? 'Unassign' : 'Assign'
                        } linked flashcard ${index + 1}`}
                        checked={isAssigned}
                        context="modal"
                        disabled={isSavingAll || Boolean(savingIds[row.id])}
                        onChange={() => void handleToggle(row.id)}
                        size="small"
                      />
                    ) : row.isSuspended ? (
                      <span
                        className="flex items-center text-fg-secondary [&_svg]:size-5"
                        title="You have suspended this flashcard."
                      >
                        <SuspendedCardsIcon />
                        <span className="sr-only">
                          Suspended flashcard {index + 1}
                        </span>
                      </span>
                    ) : (
                      // A card already in the rotation. QBank draws the clock for
                      // every one of them, because there it is always this
                      // question's assignment — the chat's read also returns the
                      // cards the student learned from another vignette, which
                      // this review will not include, so those say so instead of
                      // promising a review they are not part of (ORA-832).
                      <span
                        className="flex items-center text-fg-secondary"
                        title={
                          isAssigned
                            ? ALREADY_ASSIGNED_TITLE
                            : ALREADY_LEARNED_TITLE
                        }
                      >
                        <Clock aria-hidden="true" />
                        <span className="sr-only">
                          {isAssigned
                            ? `Flashcard ${index + 1} is already assigned`
                            : `Flashcard ${index + 1} is already learned, and is not part of this review`}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="!m-0 text-sm text-fg-secondary">{NO_CARDS}</p>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="modal_actions">
        <div className="flex w-full flex-col gap-2">
          {notice ? (
            <p className="!m-0 text-xs text-status-danger" role="alert">
              {notice}
            </p>
          ) : null}
          {info ? (
            <p className="!m-0 text-xs text-modal-fg" role="status">
              {info}
            </p>
          ) : null}
          {hint ? (
            <p className="!m-0 text-xs text-fg-secondary">{hint}</p>
          ) : null}
          {/* Stacked on a phone and reversed, so the primary action is the one
              under the student's thumb — as the variant preview does it. */}
          <div className="flex flex-col-reverse gap-2 mobile:flex-row mobile:justify-end">
            <Button
              className="min-h-11 w-full mobile:w-auto"
              context="modal"
              onClick={onClose}
              type="button"
              variant="outline"
            >
              Close
            </Button>
            {studyNowUnavailable ? null : (
              <Button
                aria-busy={isStudyNowPending || undefined}
                className="min-h-11 w-full mobile:w-auto"
                disabled={!canStudy}
                onClick={() => onStudyNow(enabledIds)}
                type="button"
              >
                {isStudyNowPending ? (
                  <Spinner size={14} thickness={3} aria-hidden="true" />
                ) : null}
                Study Now{enabledIds.length ? ` (${enabledIds.length})` : ''}
              </Button>
            )}
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default LinkedFlashcardsModal;
